import { Box, SimpleGrid, useBreakpointValue } from "@chakra-ui/react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import Widget from "./Widget";

// Splits a flat list into rows of `size` items each.
function chunk(items, size) {
    if (size <= 0) return [items];
    const rows = [];
    for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size));
    }
    return rows;
}

/**
 * Virtualized grid for the (potentially ~1000 item) non-favorites station list.
 *
 * The real scroll container for this page is the WINDOW/document: nothing constrains
 * the html/body/#root height chain, so `.main-panel`'s `max-height:100%` resolves to
 * none and it simply grows to fit its content. So we drive the virtualizer off the
 * window via `useWindowVirtualizer`. This keeps the single page scroll intact — the
 * Ranking sections and Footer below stay in normal document flow because the
 * total-size spacer reserves the full height of the list.
 */
export default function VirtualStationGrid({ stations, favorites, toggleFavorite }) {
    // Guard undefined on the very first paint before breakpoints resolve.
    const columnCount = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }) ?? 1;

    const rows = useMemo(() => chunk(stations, columnCount), [stations, columnCount]);

    const listRef = useRef(null);

    // Distance from the document top to the list start (favorites section + headers
    // render above it). Measured from the DOM after layout — reading it during render
    // returns null (0) on first paint and goes stale when the favorites section resizes.
    const [scrollMargin, setScrollMargin] = useState(0);

    useLayoutEffect(() => {
        const measure = () => setScrollMargin(listRef.current?.offsetTop ?? 0);
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
        // Re-measure when the favorites section height or column layout can shift the
        // list's vertical position in the document.
    }, [favorites.length, columnCount]);

    const virtualizer = useWindowVirtualizer({
        count: rows.length,
        // ~ card height + row gap; measureElement corrects any drift per row.
        estimateSize: () => 430,
        overscan: 3,
        scrollMargin,
    });

    return (
        <Box ref={listRef} position="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
            {virtualizer.getVirtualItems().map((vi) => {
                const row = rows[vi.index];
                return (
                    <Box
                        key={vi.key}
                        data-index={vi.index}
                        ref={virtualizer.measureElement}
                        position="absolute"
                        top={0}
                        left={0}
                        w="100%"
                        style={{
                            transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
                        }}
                    >
                        {/* Vertical gap lives as `pb` on the row so measureElement includes it. */}
                        <SimpleGrid columns={columnCount} spacing={4} pb={4}>
                            {row.map((station) => (
                                <Widget
                                    key={station.id}
                                    station={station}
                                    isFavorite={favorites.indexOf(station.identifier) !== -1}
                                    toggleFavorite={toggleFavorite}
                                />
                            ))}
                        </SimpleGrid>
                    </Box>
                );
            })}
        </Box>
    );
}
